import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, CheckCircle2, FlaskConical, Loader2, Play, Send, Wrench, X } from 'lucide-react';
import { toast } from 'sonner';
import { chatApi } from '@/api/chat';
import { proposalsApi } from '@/api/proposals';
import { unwrapError } from '@/api/client';
import { postChatMessageStream, type SseEvent } from '@/lib/sse';
import {
  CHAT_KINDS,
  type ActionFact,
  type AgentEvent,
  type ChatKind,
  type ChatSessionMeta,
  type ChatStoredMessage,
  type ChatTurnResponse,
  type Language,
  type ProposalStatus,
} from '@/api/types';

interface ProposalOutcomeView {
  proposalId: string;
  status: ProposalStatus;
  resultSummary?: string;
  resultFacts?: ActionFact[];
  error?: string;
}
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MessageTimeline } from './MessageTimeline';
import { DEFAULT_LANGUAGE, LanguageSelect } from './LanguageSelect';
import { PromptVersionSelect } from './PromptVersionSelect';

interface PlaygroundProps {
  resumeSessionId?: string | null;
  onResumeConsumed?: () => void;
  initialPromptVersion?: number | null;
}

export function Playground({
  resumeSessionId,
  onResumeConsumed,
  initialPromptVersion,
}: PlaygroundProps): JSX.Element {
  const { t } = useTranslation();
  const { credentials } = useAuth();
  const [session, setSession] = React.useState<ChatSessionMeta | null>(null);
  const [kind, setKind] = React.useState<ChatKind>('ops');
  const [title, setTitle] = React.useState(
    initialPromptVersion ? `Test v${initialPromptVersion}` : 'Playground ops',
  );
  const [promptVersion, setPromptVersion] = React.useState<number | null>(initialPromptVersion ?? null);
  const [language, setLanguage] = React.useState<Language>(DEFAULT_LANGUAGE);
  const [messages, setMessages] = React.useState<ChatStoredMessage[]>([]);
  const [composer, setComposer] = React.useState('');
  const [running, setRunning] = React.useState<RunningState | null>(null);
  const [resuming, setResuming] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const resumeDepsRef = React.useRef({ t, onResumeConsumed });
  resumeDepsRef.current = { t, onResumeConsumed };

  React.useEffect(() => {
    if (!resumeSessionId) return;
    const { t, onResumeConsumed } = resumeDepsRef.current;
    setResuming(true);
    let cancelled = false;
    (async () => {
      try {
        const detail = await chatApi.getSession(resumeSessionId, { limit: 1 });
        const all = await chatApi.getAllMessages(resumeSessionId);
        if (cancelled) return;
        setSession(detail.session);
        setMessages(all);
        toast.success(t('chat.playground.resumedToast'));
      } catch (err) {
        if (!cancelled) toast.error(unwrapError(err));
      } finally {
        if (!cancelled) {
          setResuming(false);
          onResumeConsumed?.();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [resumeSessionId]);

  const [outcomeById, setOutcomeById] = React.useState<Record<string, ProposalOutcomeView>>({});

  const proposalByMessageId = React.useMemo(() => {
    const m = new Map<string, ProposalOutcomeView>();
    for (const msg of messages) {
      if (!msg.proposalId) continue;
      const replayed = msg.proposal;
      m.set(msg.messageId, {
        proposalId: msg.proposalId,
        status: replayed?.status ?? 'pending',
        ...(replayed?.resultSummary ? { resultSummary: replayed.resultSummary } : {}),
        ...(replayed?.resultFacts ? { resultFacts: replayed.resultFacts } : {}),
        ...(replayed?.error ? { error: replayed.error } : {}),
      });
    }
    return m;
  }, [messages]);

  const createSession = async (): Promise<void> => {
    try {
      const s = await chatApi.createSession(
        {
          kind,
          title: title.trim() || undefined,
          ...(promptVersion !== null ? { promptVersion } : {}),
        },
        language,
      );
      setSession(s);
      setMessages([]);
      toast.success(t('chat.playground.createdToast', { kind: s.kind }));
    } catch (err) {
      toast.error(unwrapError(err));
    }
  };

  const closeSession = async (): Promise<void> => {
    if (!session) return;
    if (!window.confirm(t('chat.playground.closeConfirm'))) return;
    try {
      await chatApi.closeSession(session.sessionId);
      toast.success(t('chat.playground.closedToast'));
      setSession(null);
      setMessages([]);
    } catch (err) {
      toast.error(unwrapError(err));
    }
  };

  const refreshMessages = React.useCallback(async (): Promise<void> => {
    if (!session) return;
    try {
      const all = await chatApi.getAllMessages(session.sessionId);
      setMessages(all);
    } catch (err) {
      toast.error(unwrapError(err));
    }
  }, [session]);

  const send = async (): Promise<void> => {
    if (!session || !composer.trim() || running) return;
    const userText = composer.trim();
    setComposer('');

    const tempId = `temp-${Date.now()}`;
    const tempUser: ChatStoredMessage = {
      sessionId: session.sessionId,
      messageId: tempId,
      role: 'user',
      content: userText,
      createdAt: Math.floor(Date.now() / 1000),
    };
    setMessages((prev) => [...prev, tempUser]);
    setRunning({ iteration: -1, currentTool: null, streamedText: '', toolHistory: [] });

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      await postChatMessageStream({
        sessionId: session.sessionId,
        message: userText,
        signal: ctrl.signal,
        onEvent: (ev) => handleSseEvent(ev, {
          setRunning,
          onDone: (turn) => {
            setMessages((prev) => [
              ...prev.filter((m) => m.messageId !== tempId),
              turn.userMessage,
              turn.assistantMessage,
            ]);
            setRunning(null);
            if (turn.decision.kind === 'proposal') {
              toast.info(t('chat.playground.proposalToast'));
            } else if (turn.decision.kind === 'failed') {
              toast.error(t('chat.playground.failedToast', { reason: turn.decision.reason }));
            }
          },
          onError: (msg) => {
            setMessages((prev) => prev.filter((m) => m.messageId !== tempId));
            setRunning(null);
            toast.error(msg);
          },
          invalidDoneMsg: t('chat.playground.invalidDone'),
          sseErrorMsg: t('chat.playground.sseError'),
          sseErrorOpaqueMsg: t('chat.playground.sseErrorOpaque'),
        }),
      });
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.messageId !== tempId));
      setRunning(null);
      if ((err as Error).name !== 'AbortError') {
        toast.error(unwrapError(err));
      }
    } finally {
      abortRef.current = null;
    }
  };

  const abort = (): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(null);
    toast(t('chat.playground.streamAborted'));
  };

  const confirmProposal = async (proposalId: string): Promise<void> => {
    try {
      const res = await proposalsApi.confirm(proposalId);
      setOutcomeById((prev) => ({
        ...prev,
        [proposalId]: {
          proposalId,
          status: res.status as ProposalStatus,
          ...(res.resultSummary ? { resultSummary: res.resultSummary } : {}),
          ...(res.resultFacts ? { resultFacts: res.resultFacts } : {}),
          ...(res.error ? { error: res.error } : {}),
        },
      }));
      if (res.status === 'executed') {
        toast.success(t('chat.playground.executedToast'));
      } else if (res.status === 'failed') {
        toast.error(t('chat.playground.executionFailedToast', { error: res.error ?? '—' }));
      } else {
        toast.info(t('chat.playground.statusToast', { status: res.status }));
      }
      await refreshMessages();
    } catch (err) {
      toast.error(unwrapError(err));
    }
  };

  const rejectProposal = async (proposalId: string): Promise<void> => {
    try {
      const res = await proposalsApi.reject(proposalId);
      setOutcomeById((prev) => ({
        ...prev,
        [proposalId]: { proposalId, status: res.status as ProposalStatus },
      }));
      toast.success(t('chat.playground.rejectedToast'));
      await refreshMessages();
    } catch (err) {
      toast.error(unwrapError(err));
    }
  };

  if (resuming) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('common.loading')}
        </CardContent>
      </Card>
    );
  }

  if (!session) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('chat.playground.newSession')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('chat.playground.newSessionDesc', { email: credentials?.email ?? '' })}
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t('chat.playground.kind')}</Label>
              <Select value={kind} onValueChange={(v: ChatKind) => setKind(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHAT_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">{t('chat.playground.kindHint')}</p>
            </div>
            <LanguageSelect value={language} onChange={setLanguage} />
            <div className="space-y-1.5">
              <Label>{t('chat.playground.title')}</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                placeholder={t('chat.playground.titlePlaceholder')}
              />
            </div>
          </div>
          <div className="md:max-w-md">
            <PromptVersionSelect value={promptVersion} onChange={setPromptVersion} />
          </div>
          <div className="flex justify-end">
            <Button onClick={createSession} className="gap-2">
              <Play className="h-4 w-4" />
              {t('chat.playground.start')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <Card className="flex h-[calc(100vh-220px)] flex-col">
        <CardHeader className="flex-row items-center justify-between space-y-0 border-b pb-3">
          <div>
            <CardTitle className="text-base">
              {session.title ?? t('chat.playground.playground')}
            </CardTitle>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
              <Badge variant="outline">{session.kind}</Badge>
              <Badge variant="outline">{session.language}</Badge>
              {session.pinnedPromptVersion ? (
                <Badge variant="warning" className="gap-1">
                  <FlaskConical className="h-3 w-3" />
                  {t('promptVersions.pinnedBadge', { version: session.pinnedPromptVersion })}
                </Badge>
              ) : null}
              <code className="font-mono">{session.sessionId.slice(0, 8)}…</code>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={closeSession}>
            <X className="mr-1 h-3 w-3" />
            {t('chat.playground.close')}
          </Button>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-4">
          <MessageTimeline
            messages={messages}
            autoScroll
            renderFooter={(msg) => {
              const replayed = proposalByMessageId.get(msg.messageId);
              if (!replayed || msg.role !== 'assistant') return null;
              const view = outcomeById[replayed.proposalId] ?? replayed;
              return (
                <ProposalActions
                  view={view}
                  onConfirm={() => confirmProposal(view.proposalId)}
                  onReject={() => rejectProposal(view.proposalId)}
                />
              );
            }}
          />
          {running ? <RunningIndicator state={running} /> : null}
        </CardContent>
        <div className="border-t p-3">
          <Composer
            value={composer}
            onChange={setComposer}
            onSend={send}
            disabled={!!running}
            onAbort={abort}
            running={!!running}
          />
        </div>
      </Card>

      <Card className="lg:max-h-[calc(100vh-220px)] lg:overflow-y-auto">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('chat.playground.sessionPanel')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          <Row k={t('chat.playground.id')} v={session.sessionId} mono />
          <Row k={t('chat.playground.status')} v={session.status} />
          <Row k={t('chat.playground.bot')} v={session.botId} mono />
          <Row
            k={t('promptVersions.select.label')}
            v={
              session.pinnedPromptVersion
                ? t('promptVersions.pinnedRow', { version: session.pinnedPromptVersion })
                : t('promptVersions.select.active')
            }
          />
          {session.llmModel ? <Row k={t('promptVersions.fields.llmModel')} v={session.llmModel} mono /> : null}
          <Row k={t('chat.playground.user')} v={session.userId} mono />
          <Row k={t('chat.playground.messages')} v={String(messages.length)} />
        </CardContent>
      </Card>
    </div>
  );
}

interface RunningState {
  iteration: number;
  currentTool: string | null;
  streamedText: string;
  toolHistory: Array<{ name: string; status: 'started' | 'done' | 'error'; result?: unknown }>;
}

interface SseHandlerCallbacks {
  setRunning: React.Dispatch<React.SetStateAction<RunningState | null>>;
  onDone: (turn: ChatTurnResponse) => void;
  onError: (message: string) => void;
  invalidDoneMsg: string;
  sseErrorMsg: string;
  sseErrorOpaqueMsg: string;
}

function handleSseEvent(ev: SseEvent, cb: SseHandlerCallbacks): void {
  if (ev.event === 'done') {
    try {
      const payload = JSON.parse(ev.data) as ChatTurnResponse;
      cb.onDone(payload);
    } catch {
      cb.onError(cb.invalidDoneMsg);
    }
    return;
  }
  if (ev.event === 'error') {
    try {
      const payload = JSON.parse(ev.data) as { error?: string; message?: string };
      cb.onError(payload.error ?? payload.message ?? cb.sseErrorMsg);
    } catch {
      cb.onError(cb.sseErrorOpaqueMsg);
    }
    return;
  }

  let parsed: AgentEvent;
  try {
    parsed = JSON.parse(ev.data) as AgentEvent;
  } catch {
    return;
  }

  cb.setRunning((prev) => {
    if (!prev) return prev;
    switch (parsed.type) {
      case 'iteration_start':
        return { ...prev, iteration: parsed.iteration };
      case 'tool_call_start':
        return {
          ...prev,
          currentTool: parsed.name,
          toolHistory: [...prev.toolHistory, { name: parsed.name, status: 'started' }],
        };
      case 'tool_call_end': {
        const last = prev.toolHistory[prev.toolHistory.length - 1];
        const next = [...prev.toolHistory];
        if (last && last.name === parsed.name && last.status === 'started') {
          next[next.length - 1] = {
            ...last,
            status: parsed.error ? 'error' : 'done',
            result: parsed.result,
          };
        }
        return { ...prev, currentTool: null, toolHistory: next };
      }
      case 'assistant_text':
        return { ...prev, streamedText: parsed.text };
      default:
        return prev;
    }
  });
}

function RunningIndicator({ state }: { state: RunningState }): JSX.Element {
  const { t } = useTranslation();
  const iterLabel = state.currentTool
    ? t('chat.playground.iterCall', { n: state.iteration + 1, tool: state.currentTool })
    : t('chat.playground.iter', { n: state.iteration + 1 });
  return (
    <div className="mt-4 flex gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
        <Bot className="h-4 w-4" />
      </div>
      <div className="flex max-w-[85%] flex-col gap-1.5">
        <div className="rounded-lg border bg-card px-3 py-2 text-sm">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>{iterLabel}</span>
          </div>
          {state.streamedText ? (
            <p className="mt-1 whitespace-pre-wrap text-sm">{state.streamedText}</p>
          ) : null}
        </div>
        {state.toolHistory.length > 0 ? (
          <div className="w-full space-y-0.5">
            {state.toolHistory.map((tc, i) => (
              <div
                key={`${tc.name}-${i}`}
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
              >
                {tc.status === 'started' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : tc.status === 'error' ? (
                  <X className="h-3 w-3 text-destructive" />
                ) : (
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                )}
                <Wrench className="h-3 w-3" />
                <code className="font-mono">{tc.name}</code>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProposalActions({
  view,
  onConfirm,
  onReject,
}: {
  view: ProposalOutcomeView;
  onConfirm: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const settled = view.status !== 'pending';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[10px] uppercase text-muted-foreground">
        {t('chat.playground.proposal', { id: view.proposalId.slice(0, 8) })}…
      </div>
      {settled ? (
        <ProposalOutcome view={view} />
      ) : (
        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('chat.playground.confirm')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onReject();
              } finally {
                setBusy(false);
              }
            }}
          >
            {t('chat.playground.reject')}
          </Button>
        </div>
      )}
    </div>
  );
}

function ProposalOutcome({ view }: { view: ProposalOutcomeView }): JSX.Element {
  const { t } = useTranslation();
  const variant =
    view.status === 'executed'
      ? 'success'
      : view.status === 'failed'
        ? 'destructive'
        : 'secondary';
  return (
    <div className="flex flex-col gap-1.5 rounded-md border bg-muted/40 p-2">
      <Badge variant={variant} className="w-fit">
        {t(`chat.playground.outcomeStatus.${view.status}`, { defaultValue: view.status })}
      </Badge>
      {view.resultSummary ? <p className="text-xs">{view.resultSummary}</p> : null}
      {view.error ? <p className="text-xs text-destructive">{view.error}</p> : null}
      {view.resultFacts && view.resultFacts.length > 0 ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
          {view.resultFacts.map((fact) => (
            <React.Fragment key={fact.key}>
              <dt className="text-muted-foreground">
                {t(`proposals.facts.${fact.key}`, { defaultValue: fact.key })}
              </dt>
              <dd className="font-mono">{fact.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

function Composer({
  value,
  onChange,
  onSend,
  onAbort,
  disabled,
  running,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onAbort: () => void;
  disabled: boolean;
  running: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-end gap-2">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSend();
          }
        }}
        placeholder={t('chat.playground.composerPlaceholder')}
        rows={2}
        className="resize-none"
      />
      {running ? (
        <Button variant="outline" onClick={onAbort} className="gap-1.5">
          <X className="h-4 w-4" />
          {t('chat.playground.interrupt')}
        </Button>
      ) : (
        <Button onClick={onSend} disabled={disabled || !value.trim()} className="gap-1.5">
          <Send className="h-4 w-4" />
          {t('chat.playground.send')}
        </Button>
      )}
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b py-1 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className={mono ? 'font-mono text-[10px]' : ''}>{v}</span>
    </div>
  );
}
